import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import PaymentConfirmationDialog from '@/components/PaymentConfirmationDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, User, Smartphone, FileText, Calendar, Lock, Eye, EyeOff, Camera, ZoomIn, Printer, MessageCircle, Copy, UserPlus, Upload, ReceiptText, FileDown } from 'lucide-react';
import PatternLock from '@/components/PatternLock';
import DevicePhotos from '@/components/DevicePhotos';
import RepairBudgetFields from '@/components/RepairBudgetFields';
import { formatCLP } from '@/utils/currency';
import { budgetBreakdown } from '@/utils/repairBudget';
import { cacheNativePdf, isNativeApp, openNativePdf, shareNativePdf } from '@/utils/nativePdf';
import { publicSiteUrl } from '@/utils/publicSiteUrl';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  not_repaired: { label: 'Sin reparación', color: 'bg-red-100 text-red-800 border-red-200' },
  received: { label: 'Recibido', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  diagnosis: { label: 'Diagnóstico', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  in_repair: { label: 'En Reparación', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  completed: { label: 'Completado', color: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  delivered: { label: 'Entregado', color: 'bg-green-100 text-green-800 border-green-200' },
};

const TERMINAL_STATUSES = new Set(['delivered', 'not_repaired']);

const confirmTerminalStatus = (status) => {
  if (!TERMINAL_STATUSES.has(status)) return true;
  const label = STATUS_CONFIG[status].label;
  return window.confirm(`¿Confirmas cambiar la orden a ${label}? Después de aceptar no podrás modificar nuevamente su estado.`);
};

const whatsappNumber = (value) => {
  let number = String(value || '').replace(/\D/g, '');
  if (number.length === 9 && number.startsWith('9')) number = `56${number}`;
  return /^[1-9]\d{8,14}$/.test(number) ? number : null;
};

const RepairDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [repair, setRepair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [updateData, setUpdateData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [sharingDelivery, setSharingDelivery] = useState(false);
  const [sharingTracking, setSharingTracking] = useState(false);
  const [openingDelivery, setOpeningDelivery] = useState(false);
  const [deliveryPreview, setDeliveryPreview] = useState(null);
  const [nativeDeliveryPdfUri, setNativeDeliveryPdfUri] = useState(null);
  const deliveryFrameRef = useRef(null);
  const deliveryRequestRef = useRef(0);
  const [technicians, setTechnicians] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [showCreateTechnician, setShowCreateTechnician] = useState(false);
  const [creatingTechnician, setCreatingTechnician] = useState(false);
  const [newTechnician, setNewTechnician] = useState({ name: '', email: '', password: '' });
  const [savingPayment, setSavingPayment] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newNotePrivate, setNewNotePrivate] = useState(true);
  const [savingNote, setSavingNote] = useState(false);
  const [changingNoteId, setChangingNoteId] = useState(null);
  const trackingUrl = repair?.public_token ? `${publicSiteUrl()}/public/${repair.public_token}` : null;

  useEffect(() => {
    deliveryRequestRef.current += 1;
    setOpeningDelivery(false);
    setDeliveryPreview(null);
    setPendingPayment(null);
    fetchRepair();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (editDialogOpen) fetchTechnicians();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDialogOpen]);

  useEffect(() => () => {
    if (deliveryPreview?.url) window.URL.revokeObjectURL?.(deliveryPreview.url);
  }, [deliveryPreview?.url]);

  useEffect(() => () => { deliveryRequestRef.current += 1; }, []);

  const fetchTechnicians = async () => {
    setTechniciansLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/team/technicians`, { headers: getAuthHeader() });
      setTechnicians(data);
      setUpdateData((current) => {
        if (!current.assigned_technician || data.some((technician) => technician.name === current.assigned_technician)) return current;
        return { ...current, assigned_technician: '' };
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cargar la lista de técnicos');
    } finally {
      setTechniciansLoading(false);
    }
  };

  const handleCreateTechnician = async (event) => {
    event.preventDefault();
    setCreatingTechnician(true);
    try {
      const { data } = await axios.post(`${API}/api/team`, { ...newTechnician, role: 'technician' }, { headers: getAuthHeader() });
      setTechnicians((current) => [...current, { id: data.id, name: data.name }].sort((a, b) => a.name.localeCompare(b.name, 'es')));
      setUpdateData((current) => ({ ...current, assigned_technician: data.name }));
      setNewTechnician({ name: '', email: '', password: '' });
      setShowCreateTechnician(false);
      toast.success('Técnico creado y seleccionado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo crear el técnico');
    } finally {
      setCreatingTechnician(false);
    }
  };

  const fetchRepair = async () => {
    try {
      const response = await axios.get(`${API}/api/repairs/${id}`, {
        headers: getAuthHeader()
      });
      setRepair(response.data);
      setUpdateData({
        status: response.data.status,
        diagnosis: response.data.diagnosis || '',
        budget_estimate: response.data.budget_estimate ?? '',
        vat_included: response.data.vat_included || false,
        notes: response.data.notes || '',
        notes_private: response.data.notes_private || false,
        assigned_technician: response.data.assigned_technician || '',
      });
    } catch (error) {
      toast.error('Error al cargar la orden');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (updateData.status !== repair.status && !confirmTerminalStatus(updateData.status)) return;
    setUpdating(true);
    try {
      const payload = {
        ...updateData,
        budget_estimate: updateData.budget_estimate === '' ? null : updateData.budget_estimate,
      };
      
      await axios.patch(`${API}/api/repairs/${id}`, payload, {
        headers: getAuthHeader()
      });
      
      toast.success('Orden actualizada exitosamente');
      setEditDialogOpen(false);
      fetchRepair();
    } catch (error) {
      toast.error('Error al actualizar la orden');
      console.error(error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async (event) => {
    event.preventDefault();
    if (!newNote.trim() || savingNote) return;
    setSavingNote(true);
    try {
      const { data } = await axios.post(`${API}/api/repairs/${id}/notes`, {
        text: newNote.trim(), is_private: newNotePrivate,
      }, { headers: getAuthHeader() });
      setRepair(data);
      setNewNote('');
      setNewNotePrivate(true);
      toast.success('Nota guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo guardar la nota');
    } finally {
      setSavingNote(false);
    }
  };

  const handleNoteVisibility = async (note) => {
    if (changingNoteId) return;
    setChangingNoteId(note.id);
    try {
      const { data } = await axios.patch(`${API}/api/repairs/${id}/notes/${note.id}`, {
        is_private: !note.is_private,
      }, { headers: getAuthHeader() });
      setRepair(data);
      toast.success(note.is_private ? 'Nota visible en el PDF' : 'Nota privada, excluida del PDF');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cambiar la privacidad');
    } finally {
      setChangingNoteId(null);
    }
  };

  const handleQuickStatusChange = async (status) => {
    if (status === repair.status || updating) return;
    if (TERMINAL_STATUSES.has(repair.status)) {
      toast.error(`El estado ${STATUS_CONFIG[repair.status].label} es definitivo y está bloqueado.`);
      return;
    }
    if (!confirmTerminalStatus(status)) return;
    setUpdating(true);
    try {
      const { data } = await axios.patch(`${API}/api/repairs/${id}`, { status }, {
        headers: getAuthHeader()
      });
      setRepair(data);
      setUpdateData((current) => ({ ...current, status: data.status }));
      toast.success(`Estado cambiado a ${STATUS_CONFIG[status]?.label || status}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cambiar el estado');
    } finally {
      setUpdating(false);
    }
  };

  const updatePayment = async (payload, successMessage) => {
    setSavingPayment(true);
    try {
      const { data } = await axios.patch(`${API}/api/repairs/${id}`, payload, {
        headers: getAuthHeader()
      });
      setRepair(data);
      toast.success(successMessage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo actualizar el pago');
    } finally {
      setSavingPayment(false);
    }
  };

  const handlePaidChange = (checked) => {
    if (checked === Boolean(repair?.paid) || savingPayment) return;
    setPendingPayment({ ticketNumber: repair.ticket_number, paid: checked });
  };

  const confirmPaymentChange = () => {
    if (!pendingPayment || repair?.status !== 'delivered') return;
    const { paid } = pendingPayment;
    setPendingPayment(null);
    updatePayment({ paid }, paid ? 'Orden marcada como pagada' : 'Orden marcada como pendiente de pago');
  };

  const handlePhotosChange = async (photos) => {
    const { data } = await axios.patch(`${API}/api/repairs/${id}`, { device_photos: photos }, {
      headers: getAuthHeader(),
    });
    setRepair(data);
  };

  const handleReceiptUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecciona una imagen del comprobante');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no puede superar los 10 MB');
      return;
    }
    setUploadingReceipt(true);
    try {
      const { data: signed } = await axios.get(`${API}/api/cloudinary/signature`, {
        params: { folder: 'payments' },
        headers: getAuthHeader()
      });
      const form = new FormData();
      form.append('file', file);
      form.append('api_key', signed.api_key);
      form.append('timestamp', signed.timestamp);
      form.append('signature', signed.signature);
      form.append('folder', signed.folder);
      const { data: uploaded } = await axios.post(
        `https://api.cloudinary.com/v1_1/${signed.cloud_name}/image/upload`,
        form
      );
      await updatePayment({ payment_receipt_url: uploaded.secure_url }, 'Comprobante guardado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo subir el comprobante');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const getDeliveryPdf = async () => {
    const response = await axios.get(`${API}/api/repairs/${id}/delivery-pdf`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return new Blob([response.data], { type: 'application/pdf' });
  };

  const openDeliveryPreview = async () => {
    if (!repair || openingDelivery) return;
    const request = ++deliveryRequestRef.current;
    setOpeningDelivery(true);
    try {
      const pdf = await getDeliveryPdf();
      if (request !== deliveryRequestRef.current) return;
      if (isNativeApp()) {
        const uri = await cacheNativePdf(pdf, `orden_entrega_${repair.ticket_number}`);
        if (request !== deliveryRequestRef.current) return;
        setNativeDeliveryPdfUri(uri);
        await openNativePdf(uri);
        return;
      }
      const url = window.URL.createObjectURL(pdf);
      if (request !== deliveryRequestRef.current) {
        window.URL.revokeObjectURL?.(url);
        return;
      }
      setDeliveryPreview({ url, ticketNumber: repair.ticket_number });
    } catch (error) {
      if (request === deliveryRequestRef.current) toast.error(error.response?.data?.detail || 'No se pudo mostrar el PDF de entrega');
    } finally {
      if (request === deliveryRequestRef.current) setOpeningDelivery(false);
    }
  };

  const closeDeliveryPreview = () => {
    deliveryRequestRef.current += 1;
    setOpeningDelivery(false);
    setDeliveryPreview(null);
  };

  const saveDeliveryPdf = () => {
    if (!deliveryPreview) return;
    const link = document.createElement('a');
    link.href = deliveryPreview.url;
    link.download = `orden_entrega_${deliveryPreview.ticketNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const printDeliveryPdf = () => {
    try {
      if (!deliveryFrameRef.current?.contentWindow) throw new Error('PDF no disponible');
      deliveryFrameRef.current.contentWindow.focus();
      deliveryFrameRef.current.contentWindow.print();
    } catch {
      toast.error('No se pudo imprimir el PDF. Prueba con Guardar PDF.');
    }
  };

  const handleCopyTrackingLink = async () => {
    if (!trackingUrl) return;
    try {
      let copied = false;
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(trackingUrl);
          copied = true;
        } catch { /* Some browsers require the selection fallback. */ }
      }
      if (!copied) {
        const field = document.createElement('textarea');
        field.value = trackingUrl;
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        try {
          field.select();
          if (!document.execCommand('copy')) throw new Error('No se pudo copiar');
        } finally {
          field.remove();
        }
      }
      toast.success('Enlace de seguimiento copiado');
    } catch {
      toast.error('No se pudo copiar el enlace. Selecciónalo y cópialo manualmente.');
    }
  };

  const handleShareTracking = async () => {
    if (!trackingUrl || sharingTracking) return;
    setSharingTracking(true);
    try {
      const { data: customer } = await axios.get(`${API}/api/customers/${repair.customer_id}`, { headers: getAuthHeader() });
      const phone = whatsappNumber(customer.phone);
      if (!phone) {
        toast.error('Este cliente no tiene un número de WhatsApp válido. Puedes copiar el enlace.');
        return;
      }
      const message = `Hola ${customer.name || repair.customer_name}, puedes consultar el estado de tu reparación ${repair.ticket_number} aquí: ${trackingUrl}`;
      const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      const opened = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      if (!opened) window.location.href = whatsappUrl;
      toast.info('WhatsApp se abrió con el enlace. Revisa el destinatario y envía el mensaje.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo preparar el mensaje de seguimiento');
    } finally {
      setSharingTracking(false);
    }
  };

  const handleShareDelivery = async () => {
    setSharingDelivery(true);
    try {
      const [pdf, customerResponse] = await Promise.all([
        getDeliveryPdf(),
        axios.get(`${API}/api/customers/${repair.customer_id}`, { headers: getAuthHeader() }),
      ]);
      const customer = customerResponse.data;
      const filename = `orden_entrega_${repair.ticket_number}.pdf`;
      const message = `Hola ${customer.name || repair.customer_name}, te enviamos la orden ${repair.ticket_number} de ${repair.device_brand} ${repair.device_model}. Seguimiento: ${trackingUrl || publicSiteUrl()}`;

      if (isNativeApp()) {
        const uri = await cacheNativePdf(pdf, `orden_entrega_${repair.ticket_number}`);
        setNativeDeliveryPdfUri(uri);
        await shareNativePdf(uri, `Orden ${repair.ticket_number}`, message);
        return;
      }

      const file = new File([pdf], filename, { type: 'application/pdf' });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: `Orden ${repair.ticket_number}`, text: message, files: [file] });
        toast.success('Orden compartida');
        return;
      }

      const phone = whatsappNumber(customer.phone);
      if (!phone) {
        toast.error('Este cliente no tiene un número de WhatsApp válido');
        return;
      }
      const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      const opened = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      if (!opened) window.location.href = whatsappUrl;
      toast.info('WhatsApp se abrió con los datos y el enlace de seguimiento de la orden.');
    } catch (error) {
      if (error?.name !== 'AbortError') {
        toast.error('No se pudo compartir la orden');
        console.error(error);
      }
    } finally {
      setSharingDelivery(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar esta orden?')) return;
    
    try {
      await axios.delete(`${API}/api/repairs/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Orden eliminada');
      navigate('/repairs');
    } catch (error) {
      toast.error('Error al eliminar la orden');
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No establecida';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!repair) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-600">Orden no encontrada</p>
        <Button onClick={() => navigate('/repairs')} className="mt-4">Volver</Button>
      </div>
    );
  }

  const budget = budgetBreakdown(repair.budget_estimate, repair.vat_included);

  return (
    <div className="max-w-5xl" data-testid="repair-detail-page">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/repairs')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft size={18} className="mr-2" />
          Volver
        </Button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900" data-testid="repair-ticket-number">
              {repair.ticket_number}
            </h1>
            <p className="text-sm text-zinc-600 mt-1 uppercase tracking-wider">Orden de Reparación</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => isNativeApp() ? navigate(`/print-label/${repair.id}`) : window.open(`/print-label/${repair.id}`, '_blank')}
              data-testid="print-label-button"
              title="Imprimir Etiqueta"
            >
              <Printer size={18} className="mr-2" />
              Imprimir etiqueta
            </Button>
            
            {repair.status === 'delivered' && <>
              <Button variant="outline" onClick={openDeliveryPreview} disabled={openingDelivery} className="border-green-600 text-green-700 hover:bg-green-50 dark:text-green-300 dark:hover:bg-green-950/40" data-testid="print-delivery-button">
                <FileText size={18} className="mr-2" />{openingDelivery ? 'Abriendo PDF...' : 'Ver PDF de entrega'}
              </Button>
              {isNativeApp() && nativeDeliveryPdfUri && <Button variant="outline" onClick={() => shareNativePdf(nativeDeliveryPdfUri, `Orden ${repair.ticket_number}`).catch(() => toast.error('No se pudo compartir el PDF'))}>
                <FileDown size={18} className="mr-2" />Guardar o imprimir
              </Button>}
              <Button variant="outline" onClick={handleShareDelivery} disabled={sharingDelivery} className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40" data-testid="share-delivery-button">
                <MessageCircle size={18} className="mr-2" />{sharingDelivery ? 'Preparando…' : 'Compartir entrega'}
              </Button>
            </>}
            
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="edit-repair-button">
                  <Edit size={18} className="mr-2" />
                  Editar
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Actualizar Orden</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-sm font-medium text-zinc-900">Estado</Label>
                    <Select
                      value={updateData.status}
                      onValueChange={(value) => setUpdateData({ ...updateData, status: value })}
                      disabled={TERMINAL_STATUSES.has(repair.status)}
                    >
                      <SelectTrigger className="mt-1" data-testid="update-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                          <React.Fragment key={value}>
                            <SelectItem
                              value={value}
                              className={value === 'not_repaired' ? 'text-red-700 dark:text-red-400 focus:bg-red-50 focus:text-red-800 dark:focus:bg-red-950 dark:focus:text-red-300' : undefined}
                            >{config.label}</SelectItem>
                            {value === 'not_repaired' && <SelectSeparator />}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-zinc-900">Técnico Asignado</Label>
                    <Select
                      value={updateData.assigned_technician || '__none__'}
                      onValueChange={(value) => setUpdateData({ ...updateData, assigned_technician: value === '__none__' ? '' : value })}
                      disabled={techniciansLoading}
                    >
                      <SelectTrigger className="mt-1" data-testid="update-technician-select">
                        <SelectValue placeholder={techniciansLoading ? 'Cargando técnicos…' : 'Selecciona un técnico'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sin técnico asignado</SelectItem>
                        {technicians.map((technician) => (
                          <SelectItem key={technician.id} value={technician.name}>{technician.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!techniciansLoading && technicians.length === 0 && (
                      <p className="mt-2 text-sm text-amber-700">Todavía no hay técnicos activos en el Equipo de trabajo.</p>
                    )}
                    {user?.role === 'admin' && (
                      <Button type="button" variant="link" className="mt-1 h-auto px-0" onClick={() => setShowCreateTechnician((value) => !value)}>
                        <UserPlus size={16} className="mr-2" />{showCreateTechnician ? 'Cancelar creación' : 'Crear técnico aquí'}
                      </Button>
                    )}
                    {showCreateTechnician && user?.role === 'admin' && (
                      <form onSubmit={handleCreateTechnician} className="mt-3 space-y-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-900">
                        <p className="text-sm font-medium">Nuevo técnico</p>
                        <Input required aria-label="Nombre del nuevo técnico" placeholder="Nombre" value={newTechnician.name} onChange={(event) => setNewTechnician({ ...newTechnician, name: event.target.value })} />
                        <Input required type="email" aria-label="Correo del nuevo técnico" placeholder="Correo" autoComplete="off" value={newTechnician.email} onChange={(event) => setNewTechnician({ ...newTechnician, email: event.target.value })} />
                        <Input required type="password" aria-label="Contraseña del nuevo técnico" placeholder="Contraseña de al menos 10 caracteres" minLength={10} maxLength={72} autoComplete="new-password" value={newTechnician.password} onChange={(event) => setNewTechnician({ ...newTechnician, password: event.target.value })} />
                        <Button type="submit" size="sm" disabled={creatingTechnician}>{creatingTechnician ? 'Creando…' : 'Crear y seleccionar'}</Button>
                      </form>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-zinc-900">Diagnóstico</Label>
                    <Textarea
                      value={updateData.diagnosis}
                      onChange={(e) => setUpdateData({ ...updateData, diagnosis: e.target.value })}
                      className="mt-1 min-h-[100px]"
                      data-testid="update-diagnosis-input"
                    />
                  </div>

                  <RepairBudgetFields
                    idPrefix="edit-repair"
                    amountTestId="update-budget-input"
                    amount={updateData.budget_estimate}
                    vatIncluded={!!updateData.vat_included}
                    onChange={({ amount, vatIncluded }) => setUpdateData((current) => ({
                      ...current, budget_estimate: amount, vat_included: vatIncluded,
                    }))}
                  />

                  <div>
                    <Label className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Nota inicial</Label>
                    <Textarea
                      value={updateData.notes}
                      onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                      className="mt-1"
                      data-testid="update-notes-input"
                    />
                    <div className="mt-3 flex items-center gap-3">
                      <Switch id="initial-note-private" checked={!!updateData.notes_private} onCheckedChange={(checked) => setUpdateData({ ...updateData, notes_private: checked })} data-testid="initial-note-private-switch" />
                      <Label htmlFor="initial-note-private" className="text-sm dark:text-zinc-100">Privada: no aparece en el PDF de entrega</Label>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setEditDialogOpen(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleUpdate}
                      disabled={updating}
                      className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                      data-testid="save-repair-button"
                    >
                      {updating ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="delete-repair-button"
            >
              <Trash2 size={18} />
            </Button>
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30" data-testid="repair-tracking-actions">
        <div>
          <p className="font-semibold text-blue-950 dark:text-blue-100">Seguimiento del cliente</p>
          {trackingUrl ? (
            <p className="mt-1 break-all text-sm text-blue-800 dark:text-blue-200">{trackingUrl}</p>
          ) : (
            <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">Esta orden no tiene enlace de seguimiento disponible.</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleCopyTrackingLink} disabled={!trackingUrl} data-testid="copy-tracking-link-button">
            <Copy size={16} className="mr-2" />Copiar enlace
          </Button>
          <Button variant="outline" onClick={handleShareTracking} disabled={!trackingUrl || sharingTracking} data-testid="share-tracking-link-button">
            <MessageCircle size={16} className="mr-2" />{sharingTracking ? 'Preparando…' : 'Enviar seguimiento al cliente'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white border border-zinc-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl font-medium">
                <User size={20} />
                Información del Cliente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Cliente</p>
                  <p data-testid="customer-name">
                    <button type="button" onClick={() => navigate(`/customers/${encodeURIComponent(repair.customer_id)}`)}
                      className="text-left text-base font-medium text-blue-700 underline-offset-2 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-300"
                      aria-label={`Ver ficha e historial de ${repair.customer_name}`}
                      data-testid="repair-customer-profile-link">
                      {repair.customer_name}
                    </button>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-zinc-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl font-medium">
                <Smartphone size={20} />
                Información del Equipo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Marca</p>
                  <p className="text-base font-medium text-zinc-900">{repair.device_brand}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Modelo</p>
                  <p className="text-base font-medium text-zinc-900">{repair.device_model}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">IMEI</p>
                  <p className="text-base font-medium text-zinc-900 font-mono text-sm" data-testid="device-imei">{repair.device_imei}</p>
                </div>
                {repair.device_serial && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Número de Serie</p>
                    <p className="text-base font-medium text-zinc-900 font-mono text-sm">{repair.device_serial}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {(!TERMINAL_STATUSES.has(repair.status) || (repair.device_photos && repair.device_photos.length > 0)) && (
            <Card className="bg-white border border-zinc-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-medium">
                  <Camera size={20} />
                  Fotos del Equipo
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!TERMINAL_STATUSES.has(repair.status) ? (
                  <DevicePhotos
                    photos={repair.device_photos || []}
                    onChange={handlePhotosChange}
                    maxPhotos={5}
                    authHeader={getAuthHeader()}
                    onPhotoClick={setSelectedPhoto}
                  />
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {repair.device_photos.map((photo, index) => (
                      <div
                        key={index}
                        className="relative group rounded-lg overflow-hidden border-2 border-zinc-200 hover:border-blue-400 transition-colors cursor-pointer"
                        onClick={() => setSelectedPhoto(photo)}
                        data-testid={`device-photo-${index}`}
                      >
                        <img
                          src={photo}
                          alt={`Foto del equipo ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                          <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs py-1 px-2">
                          Foto {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="bg-white border border-zinc-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl font-medium">
                <FileText size={20} />
                Detalles del Servicio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Problema Reportado</p>
                <p className="text-base text-zinc-900" data-testid="reported-issue">{repair.reported_issue}</p>
              </div>
              {repair.diagnosis && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Diagnóstico</p>
                  <p className="text-base text-zinc-900" data-testid="diagnosis">{repair.diagnosis}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border border-zinc-200 shadow-sm dark:bg-zinc-900 dark:border-zinc-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl font-medium">
                <FileText size={20} /> Notas de la orden
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {repair.notes && (
                <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold uppercase text-zinc-500">Nota inicial</span>
                    <Badge variant="outline" className={repair.notes_private ? 'border-amber-300 text-amber-700 dark:text-amber-300' : 'border-green-300 text-green-700 dark:text-green-300'}>
                      {repair.notes_private ? 'Privada · no sale en PDF' : 'Visible en PDF'}
                    </Badge>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm text-zinc-900 dark:text-zinc-100">{repair.notes}</p>
                  <Button type="button" variant="link" className="mt-1 h-auto px-0 text-xs" onClick={() => setEditDialogOpen(true)}>Editar nota o privacidad</Button>
                </div>
              )}
              {(repair.note_entries || []).map((note) => (
                <div key={note.id} className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700" data-testid="repair-note">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={note.is_private ? 'border-amber-300 text-amber-700 dark:text-amber-300' : 'border-green-300 text-green-700 dark:text-green-300'}>
                        {note.is_private ? 'Privada · no sale en PDF' : 'Visible en PDF'}
                      </Badge>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">{note.created_by} · {formatDate(note.created_at)}</span>
                    </div>
                    <Button type="button" variant="outline" size="sm" disabled={!!changingNoteId} onClick={() => handleNoteVisibility(note)} data-testid={`toggle-note-${note.id}`}>
                      {note.is_private ? 'Incluir en PDF' : 'Hacer privada'}
                    </Button>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm text-zinc-900 dark:text-zinc-100">{note.text}</p>
                </div>
              ))}
              <form onSubmit={handleAddNote} className="space-y-3 border-t border-zinc-200 pt-4 dark:border-zinc-700">
                <Label htmlFor="new-repair-note" className="dark:text-zinc-100">Agregar nota</Label>
                <Textarea id="new-repair-note" value={newNote} onChange={(event) => setNewNote(event.target.value)} maxLength={500} placeholder="Escribe una nota para esta orden..." data-testid="new-repair-note" />
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Switch id="new-note-private" checked={newNotePrivate} onCheckedChange={setNewNotePrivate} data-testid="new-note-private-switch" />
                    <Label htmlFor="new-note-private" className="text-sm dark:text-zinc-100">Privada: no aparece en el PDF</Label>
                  </div>
                  <Button type="submit" disabled={!newNote.trim() || savingNote} data-testid="save-repair-note">
                    {savingNote ? 'Guardando...' : 'Guardar nota'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {(repair.unlock_type && repair.unlock_type !== 'none') && (
            <Card className="bg-white border border-zinc-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-medium">
                  <Lock size={20} />
                  Contraseña de Desbloqueo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Tipo</p>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 border font-medium">
                    {repair.unlock_type === 'numeric' && 'PIN Numérico'}
                    {repair.unlock_type === 'alphanumeric' && 'Contraseña Alfanumérica'}
                    {repair.unlock_type === 'pattern' && 'Patrón de Desbloqueo'}
                  </Badge>
                </div>

                {repair.unlock_type === 'pattern' && repair.unlock_pattern && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Patrón</p>
                    <div className="bg-zinc-50 p-4 rounded-lg border border-zinc-200">
                      <PatternLock
                        value={JSON.parse(repair.unlock_pattern)}
                        onChange={() => {}}
                        disabled={true}
                      />
                    </div>
                  </div>
                )}

                {(repair.unlock_type === 'numeric' || repair.unlock_type === 'alphanumeric') && repair.unlock_password && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Contraseña</p>
                    <div className="flex items-center gap-2">
                      <code className={`flex-1 p-3 bg-zinc-50 border border-zinc-200 rounded-md font-mono text-lg tracking-wider ${
                        showPassword ? 'text-zinc-900' : 'text-zinc-400'
                      }`}>
                        {showPassword ? repair.unlock_password : '•'.repeat(repair.unlock_password.length)}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-xs text-amber-900">
                    <strong>Nota:</strong> Esta información es confidencial y solo debe ser usada por técnicos autorizados.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card className="bg-white border border-zinc-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-medium">Estado</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={repair.status}
                onValueChange={handleQuickStatusChange}
                disabled={updating || TERMINAL_STATUSES.has(repair.status)}
              >
                <SelectTrigger
                  className={`w-full border font-medium text-base ${STATUS_CONFIG[repair.status]?.color}`}
                  data-testid="quick-status-select"
                  aria-label="Cambiar estado de la reparación"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                    <React.Fragment key={value}>
                      <SelectItem
                        value={value}
                        className={value === 'not_repaired' ? 'text-red-700 dark:text-red-400 focus:bg-red-50 focus:text-red-800 dark:focus:bg-red-950 dark:focus:text-red-300' : undefined}
                      >{config.label}</SelectItem>
                      {value === 'not_repaired' && <SelectSeparator />}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                {TERMINAL_STATUSES.has(repair.status)
                  ? 'Estado final confirmado. Ya no se puede modificar.'
                  : 'Selecciona un estado para guardarlo inmediatamente.'}
              </p>
              {repair.status === 'delivered' && (
                <div className="mt-5 space-y-4 border-t border-zinc-200 pt-4 dark:border-zinc-700">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label htmlFor="paid-switch" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Pago</Label>
                      <p className={`mt-1 text-sm font-medium ${repair.paid ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {repair.paid ? 'Pagado' : 'Pendiente'}
                      </p>
                    </div>
                    <Switch
                      id="paid-switch"
                      checked={Boolean(repair.paid)}
                      onCheckedChange={handlePaidChange}
                      disabled={savingPayment || uploadingReceipt}
                      aria-label="Marcar orden como pagada"
                      data-testid="paid-switch"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Comprobante de pago</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button asChild variant="outline" size="sm" disabled={uploadingReceipt || savingPayment}>
                        <label className="cursor-pointer">
                          <Upload size={16} className="mr-2" />Galería
                          <input type="file" accept="image/*" className="hidden" onChange={handleReceiptUpload} disabled={uploadingReceipt || savingPayment} />
                        </label>
                      </Button>
                      <Button asChild variant="outline" size="sm" disabled={uploadingReceipt || savingPayment}>
                        <label className="cursor-pointer">
                          <Camera size={16} className="mr-2" />Tomar foto
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleReceiptUpload} disabled={uploadingReceipt || savingPayment} />
                        </label>
                      </Button>
                    </div>
                    {uploadingReceipt && <p className="text-xs text-blue-600">Subiendo comprobante…</p>}
                    {repair.payment_receipt_url && (
                      <button
                        type="button"
                        onClick={() => setSelectedPhoto(repair.payment_receipt_url)}
                        className="flex w-full items-center gap-3 rounded-md border border-zinc-200 p-2 text-left hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
                      >
                        <img src={repair.payment_receipt_url} alt="Comprobante de pago" className="h-14 w-14 rounded object-cover" />
                        <span className="flex items-center gap-2 text-sm font-medium"><ReceiptText size={16} />Ver comprobante</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {repair.assigned_technician && (
            <Card className="bg-white border border-zinc-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-medium">Técnico</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base font-medium text-zinc-900">{repair.assigned_technician}</p>
              </CardContent>
            </Card>
          )}

          {budget && (
            <Card className="border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
              <CardHeader>
                <CardTitle className="text-xl font-medium dark:text-zinc-100">Presupuesto</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {repair.vat_included && <>
                  <p className="flex justify-between gap-3 text-sm text-zinc-700 dark:text-zinc-300"><span>Valor neto</span><strong>{formatCLP(budget.net)}</strong></p>
                  <p className="flex justify-between gap-3 text-sm text-zinc-700 dark:text-zinc-300"><span>IVA (19%)</span><strong>{formatCLP(budget.vat)}</strong></p>
                </>}
                <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100" data-testid="budget-estimate">
                  {formatCLP(budget.total)}
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">{repair.vat_included ? 'Total con IVA · Pesos Chilenos (CLP)' : 'Pesos Chilenos (CLP)'}</p>
              </CardContent>
            </Card>
          )}

          <Card className="bg-white border border-zinc-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl font-medium">
                <Calendar size={20} />
                Fechas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Fecha de Ingreso</p>
                <p className="text-sm text-zinc-900">{formatDate(repair.received_date)}</p>
              </div>
              {repair.estimated_delivery && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Entrega Estimada</p>
                  <p className="text-sm text-zinc-900">{formatDate(repair.estimated_delivery)}</p>
                </div>
              )}
              {repair.completed_date && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Fecha de Completado</p>
                  <p className="text-sm text-zinc-900">{formatDate(repair.completed_date)}</p>
                </div>
              )}
              {repair.delivered_date && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Fecha de Entrega</p>
                  <p className="text-sm text-zinc-900">{formatDate(repair.delivered_date)}</p>
                </div>
              )}
              {repair.paid_at && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Fecha de Pago</p>
                  <p className="text-sm font-medium text-emerald-700">{formatDate(repair.paid_at)}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <PaymentConfirmationDialog
        change={pendingPayment}
        onCancel={() => setPendingPayment(null)}
        onConfirm={confirmPaymentChange}
      />

      <Dialog open={Boolean(deliveryPreview)} onOpenChange={(open) => { if (!open) closeDeliveryPreview(); }}>
        <DialogContent className="flex h-[92vh] w-[calc(100vw-1rem)] max-w-5xl flex-col overflow-hidden p-3 sm:p-5">
          {deliveryPreview && <>
            <DialogHeader>
              <DialogTitle className="pr-8">Orden de entrega · {deliveryPreview.ticketNumber}</DialogTitle>
              <DialogDescription>Revisa la orden antes de guardarla o imprimirla.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button type="button" variant="outline" onClick={closeDeliveryPreview} data-testid="delivery-pdf-back-button">
                <ArrowLeft size={16} className="mr-2" />Volver a la reparación
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={printDeliveryPdf} data-testid="delivery-pdf-print-button">
                  <Printer size={16} className="mr-2" />Imprimir
                </Button>
                <Button type="button" onClick={saveDeliveryPdf} data-testid="delivery-pdf-save-button">
                  <FileDown size={16} className="mr-2" />Guardar PDF
                </Button>
              </div>
            </div>
            <iframe ref={deliveryFrameRef} src={deliveryPreview.url} title={`PDF de entrega ${deliveryPreview.ticketNumber}`}
              className="min-h-0 w-full flex-1 rounded-md border border-zinc-200 bg-white dark:border-zinc-700" data-testid="delivery-pdf-preview" />
          </>}
        </DialogContent>
      </Dialog>

      {/* Photo Viewer Dialog */}
      <Dialog open={selectedPhoto !== null} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Vista de Foto</DialogTitle>
          </DialogHeader>
          <div className="relative">
            {selectedPhoto && (
              <img
                src={selectedPhoto}
                alt="Vista ampliada"
                className="w-full h-auto rounded-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RepairDetail;
